import {Category} from "@/hooks/useCategories.ts";
import {normalizePage} from "@/lib/paging.ts";
import api from "@/lib/api.ts";
import {keepPreviousData, QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Tag} from "@/hooks/useTags.ts";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type NewsId = string;
export type MediaId = string;

export type NewsArticle = {
    id: NewsId;
    title: string;
    slug: string;
    summary?: string;
    body: string;
    ionoLink?: string;
    language: string;
    status: string;
    category: Category;
    tags?: Tag[];
    scheduledAt?: string;
    publishedAt?: string;
    updatedAt?: string;
    media?: Media;
};

export type SaveNewsInput = {
    id?: NewsId;
    title: string;
    summary?: string | null;
    body?: string | null;
    language: string;
    categories: string;
    tags?: string[] | null;
    status: string;
    publishedAt?: string | null;
    scheduledAt?: string | null;
    ionoLink?: string | null;
    file?: File | null;
    caption?: string | null;
    onUploadProgress?: (pct: number) => void;
};

export type Media = {
    id: MediaId;
    url: string;
    type: string;
    caption?: string;
};

export type SortField = "title" | "publishedAt";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
    q?: string;
};

export type NewsPage = ReturnType<typeof normalizePage<NewsArticle>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/news";
const EDITOR_BASE = "/api/v1/editor/news";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "title" as SortField,
    sortOrder: "desc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const newsKeys = {
    all: ["news"] as const,
    list: (fetchAll: boolean, params?: HookParams) =>
        [...newsKeys.all, "list", fetchAll, params ?? {}] as const,
    byCategorySlug: (slug: string, params?: HookParams) =>
        [...newsKeys.all, "byCategorySlug", slug, params ?? {}] as const,
    byTagSlug: (slug: string, params?: HookParams) =>
        [...newsKeys.all, "byTagSlug", slug, params ?? {}] as const,
    bySlug: (slug: string) => [...newsKeys.all, "bySlug", slug] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
const MIN_SEARCH_LEN = 3;

function sanitizeQ(q?: string | null) {
    const v = (q ?? "").trim();
    return v.length >= MIN_SEARCH_LEN ? v : "";
}

function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): NewsPage {
    return normalizePage<NewsArticle>({page, size, total: 0, content: []});
}

async function fetchNews(fetchAll: boolean = false, params: HookParams = {}): Promise<NewsPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;
    const q = sanitizeQ(params.q);

    const sort = `${sortBy},${sortOrder}`;
    const base = fetchAll ? EDITOR_BASE : PUBLIC_BASE;

    const endpoint = q ? `${base}/search` : base;
    const {data} = await api.get(endpoint, {
        params: q ? {q, page, size, sort} : {page, size, sort},
    });

    if (Array.isArray(data?.content)) return normalizePage<NewsArticle>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchNewsByCategorySlug(slug: string, params: HookParams = {}): Promise<NewsPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(`${PUBLIC_BASE}/category/${slug}`, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<NewsArticle>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchNewsByTagSlug(slug: string, params: HookParams = {}): Promise<NewsPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(`${PUBLIC_BASE}/tag/${slug}`, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<NewsArticle>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

async function fetchNewsBySlug(slug: string): Promise<NewsArticle> {
    const {data} = await api.get(`${PUBLIC_BASE}/${slug}`);
    return data;
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useNews(fetchAll: boolean, params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
        q: params?.q?.trim(),
    };

    return useQuery({
        queryKey: newsKeys.list(fetchAll, q),
        queryFn: () => fetchNews(fetchAll, q),
        retry: 1,
        staleTime: 60_000,
        gcTime: 10 * 60_000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
    });
}

export function useNewsByCategorySlug(slug: string, params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: newsKeys.byCategorySlug(slug, q),
        queryFn: () => fetchNewsByCategorySlug(slug, q),
        enabled: Boolean(slug),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useNewsByTagSlug(slug: string, params?: HookParams) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: newsKeys.byTagSlug(slug, q),
        queryFn: () => fetchNewsByTagSlug(slug, q),
        enabled: Boolean(slug),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

export function useNewsBySlug(slug: string) {
    return useQuery({
        queryKey: newsKeys.bySlug(slug),
        queryFn: () => fetchNewsBySlug(slug),
        enabled: Boolean(slug),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveNews() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...newsKeys.all, "save"],
        mutationFn: async (payload: SaveNewsInput): Promise<NewsArticle> => {
            const body = {
                title: payload.title.trim(),
                summary: payload.summary?.trim() || undefined,
                body: payload.body?.trim() || undefined,
                ionoLink: payload.ionoLink?.trim() || undefined,
                language: payload.language,
                categories: [payload.categories],
                tags: payload.tags?.map((t) => t.trim()).filter((t) => t) || [],
                status: payload.status,
                publishedAt: payload.publishedAt,
                scheduledAt: payload.scheduledAt
            }

            const saved: NewsArticle =
                payload.id
                    ? (await api.put(`${EDITOR_BASE}/${payload.id}`, body)).data
                    : (await api.post(EDITOR_BASE, body)).data;

            if (payload.file) {
                const fd = new FormData();
                fd.append("file", payload.file);
                if (payload.caption?.trim()) fd.append("caption", payload.caption.trim());

                await api.post(`${EDITOR_BASE}/${saved.id}/media`, fd, {
                    params: {type: "IMAGE"},
                    headers: {"Content-Type": "multipart/form-data"},
                    onUploadProgress: (e) => {
                        if (e.total && payload.onUploadProgress) {
                            payload.onUploadProgress(Math.round((e.loaded * 100) / e.total));
                        }
                    },
                });
            }
            return saved;
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: newsKeys.all});
        },
    });
}

export function useDeleteNews() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...newsKeys.all, "delete"],
        mutationFn: async (id: NewsId) => {
            await api.delete(`${EDITOR_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            await qc.cancelQueries({queryKey: newsKeys.all});

            const snapshots = qc.getQueriesData<unknown>({queryKey: newsKeys.all});

            // Optimistically remove the banner from any paged lists we find
            snapshots.forEach(([key, value]) => {
                const page = value as Partial<NewsPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: NewsPage = {
                        ...(page as NewsPage),
                        items: page.items.filter((b: any) => b.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};

        },
        onError: (e, _id, ctx) => {
            // Roll back from snapshots if we have them
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            // Ensure server truth wins in the end
            qc.invalidateQueries({queryKey: newsKeys.all});
        },
    });
}
